import React, { useContext } from "react";
import { QueryKey, useInfiniteQuery } from "react-query";
import { CellClickedEvent } from "@ag-grid-community/core";
import { useHistory, useRouteMatch } from "react-router-dom";

import { PermissionLevel } from "fe-shared/src/types/domain";
import { hasPermission } from "fe-shared/src/helpers/permissions";

import useQueryFilters from "fe-shared-transactions/src/hooks/useQueryFilters";

import UserContext from "../../contexts/userContext";
import {
  INVENTORY_ADJUSTMENTS_QUERY_KEY,
  PAGE_SIZE,
  PERMISSIONS,
} from "../../consts";
import {
  GridInventoryAdjustment,
  GridInventoryAdjustmentList,
} from "../../types/inventoryAdjustments";
import useInventoryAdjustmentsRequestParams from "../../hooks/useInventoryAdjustmentsRequestParams";
import InventoryAdjustmentsDataGrid from "../../components/InventoryAdjustmensDataGrid/InventoryAdjustmentsDataGrid";

import * as api from "./modules/api";
import { getInventoryAdjustmentFilterParams } from "./modules/helpers";

interface Props {
  lastOpenedNumber: string | null;
  clearLastOpenedNumber: () => void;
}

const InventoryAdjustments = ({
  lastOpenedNumber,
  clearLastOpenedNumber,
}: Props) => {
  const history = useHistory();
  const match = useRouteMatch();
  const user = useContext(UserContext);
  const {
    requestParams,
    setFilters,
    ...otherProps
  } = useInventoryAdjustmentsRequestParams();

  useQueryFilters(setFilters, getInventoryAdjustmentFilterParams);

  const canViewList = hasPermission(user?.permissions, {
    name: PERMISSIONS.inventoryAdjustmentsList,
    level: PermissionLevel.Read,
  });

  const canViewDetails = hasPermission(user?.permissions, {
    name: PERMISSIONS.inventoryAdjustmentsDetails,
    level: PermissionLevel.Read,
  });

  const canExport = hasPermission(user?.permissions, {
    name: PERMISSIONS.inventoryAdjustmentsListExport,
    level: PermissionLevel.Full,
  });

  const canAssign = hasPermission(user?.permissions, {
    name: PERMISSIONS.inventoryAdjustmentsAssign,
    level: PermissionLevel.Full,
  });

  const adjustmentQueryKey: QueryKey = [
    INVENTORY_ADJUSTMENTS_QUERY_KEY,
    requestParams,
  ];
  const inventoryAdjustments = useInfiniteQuery<
    GridInventoryAdjustmentList<GridInventoryAdjustment>
  >(adjustmentQueryKey, api.getInventoryAdjustments, {
    enabled: canViewList,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    getFetchMore: (lastGroup, allGroups) =>
      allGroups.length * PAGE_SIZE < lastGroup.total
        ? allGroups.length + 1
        : undefined,
  });

  const onCellClicked = (e: CellClickedEvent) => {
    const eventTags = ["BUTTON", "A"];
    const currentNode = (e.event?.target as Element).nodeName;
    const parentNode = (e.event?.target as Element).parentNode?.nodeName || "";
    const isCurrentTagClicked = eventTags.includes(currentNode);
    const isParentTagClicked = eventTags.includes(parentNode);
    const colId = e.column.getColId();

    if (
      canViewDetails &&
      !isCurrentTagClicked &&
      !isParentTagClicked &&
      colId !== "assigneeDisplayName"
    ) {
      history.push(`${match.url}/${e.data.overallAdjustmentNumber}`);
    }
  };

  return canViewList ? (
    <InventoryAdjustmentsDataGrid
      inventoryAdjustments={inventoryAdjustments}
      lastOpenedNumber={lastOpenedNumber}
      clearLastOpenedNumber={clearLastOpenedNumber}
      onCellClicked={onCellClicked}
      onCellMouseDown={(e) => {
        const colId = e.column.getColId();

        if (colId === "assigneeDisplayName") {
          e.api.ensureColumnVisible(colId);
        }
      }}
      setFilters={setFilters}
      canExport={canExport}
      canAssign={canAssign}
      listQueryKey={adjustmentQueryKey}
      {...otherProps}
    />
  ) : null;
};

export default InventoryAdjustments;
